import { useMemo, useState, type ReactNode } from "react";
import type { OrganisationRecordingSetting } from "./access.ts";
import { ColumnFilter, FilterChips, type FilterChip, type FilterChoice } from "./columnFilter.tsx";
import { DataTable, type DataColumn } from "./dataTable.tsx";
import { flattenRecordingPolicyRows, type RecordingPolicyRow, type RecordingTarget } from "./recordingPolicyFlatten.ts";
import { SETTINGS_POLICIES_TABLE_ID, SETTINGS_REASON_TABLE_ID } from "./tableState.ts";

export type { RecordingPolicyRow, RecordingTarget };

export type ReasonPolicyRow = {
  key: string;
  name: string;
  kind: string;
  reasonLabel: string;
  effectiveLabel: string;
  organisationId: string;
  target: RecordingTarget;
};

const POLICY_SCOPE_COLUMN_LABEL = "Scope";

const RECORDING_COLUMNS = ["name", "kind", "recording", "stealth", "effective"] as const;
const REASON_COLUMNS = ["name", "kind", "reason", "effective"] as const;

type RecordingColumn = (typeof RECORDING_COLUMNS)[number];
type ReasonColumn = (typeof REASON_COLUMNS)[number];

type Filters<Column extends string> = { values: Map<Column, Set<string>> };

function toggle<Column extends string>(filters: Filters<Column>, column: Column, value: string): Filters<Column> {
  const values = new Map(filters.values);
  const selected = new Set(values.get(column) ?? []);
  if (selected.has(value)) selected.delete(value);
  else selected.add(value);
  if (selected.size === 0) values.delete(column);
  else values.set(column, selected);
  return { values };
}

function filterRows<Row, Column extends string>(
  rows: Row[],
  filters: Filters<Column>,
  facet: (row: Row, column: Column) => string,
  columns: readonly Column[],
): Row[] {
  return rows.filter((row) =>
    columns.every((column) => {
      const selected = filters.values.get(column);
      if (!selected || selected.size === 0) return true;
      return selected.has(facet(row, column));
    }),
  );
}

function facetCounts<Row, Column extends string>(
  rows: Row[],
  filters: Filters<Column>,
  facet: (row: Row, column: Column) => string,
  columns: readonly Column[],
) {
  const counts = new Map<Column, Map<string, number>>(columns.map((column) => [column, new Map()]));
  for (const row of rows) {
    for (const column of columns) {
      const selected = filters.values.get(column);
      const passes = !selected || selected.size === 0 || selected.has(facet(row, column));
      const other = columns.every((item) => {
        if (item === column) return true;
        const pick = filters.values.get(item);
        return !pick || pick.size === 0 || pick.has(facet(row, item));
      });
      if (!passes && !other) continue;
      const tally = counts.get(column)!;
      const value = facet(row, column);
      tally.set(value, (tally.get(value) ?? 0) + 1);
    }
  }
  return counts;
}

function choices<Column extends string>(
  column: Column,
  counts: Map<Column, Map<string, number>>,
  filters: Filters<Column>,
  onToggle: (value: string) => void,
): FilterChoice[] {
  const tally = counts.get(column) ?? new Map();
  const selected = filters.values.get(column);
  return [...tally.keys()].sort().map((value) => ({
    key: value,
    label: value,
    count: String(tally.get(value) ?? 0),
    checked: selected?.has(value) === true,
    onToggle: () => onToggle(value),
  }));
}

function recordingFacet(row: RecordingPolicyRow, column: RecordingColumn): string {
  switch (column) {
    case "name":
      return row.name;
    case "kind":
      return row.kind;
    case "recording":
      return row.recordingLabel;
    case "stealth":
      return row.stealthLabel;
    case "effective":
      return row.effectiveLabel;
  }
}

function reasonFacet(row: ReasonPolicyRow, column: ReasonColumn): string {
  switch (column) {
    case "name":
      return row.name;
    case "kind":
      return row.kind;
    case "reason":
      return row.reasonLabel;
    case "effective":
      return row.effectiveLabel;
  }
}

export function flattenReasonPolicyRows(rows: OrganisationRecordingSetting[]): ReasonPolicyRow[] {
  const flat: ReasonPolicyRow[] = [];
  for (const org of rows) {
    flat.push({
      key: `${org.organisationId}-reason`,
      name: org.name,
      kind: "Organisation",
      reasonLabel: org.reasonRule,
      effectiveLabel: org.reasonRule === "Required" ? "Required" : "Off",
      organisationId: org.organisationId,
      target: { kind: "organisation" },
    });
    for (const department of org.departments) {
      flat.push({
        key: `${department.departmentId}-reason`,
        name: department.name,
        kind: "Department",
        reasonLabel: department.reasonLocked && department.reasonRule === "Off" ? "Inherit" : department.reasonRule,
        effectiveLabel: department.reasonEffective,
        organisationId: org.organisationId,
        target: { kind: "department", departmentId: department.departmentId },
      });
    }
    for (const tag of org.tags) {
      const name = tag.value.length > 0 ? `${tag.key}:${tag.value}` : tag.key;
      flat.push({
        key: `${tag.key}:${tag.value}-reason`,
        name,
        kind: "Tag",
        reasonLabel: tag.reasonLocked && tag.reasonRule === "Off" ? "Inherit" : tag.reasonRule,
        effectiveLabel: tag.reasonEffective,
        organisationId: org.organisationId,
        target: { kind: "tag", key: tag.key, value: tag.value },
      });
    }
  }
  return flat;
}

export function RecordingPolicyTable(props: {
  rows: OrganisationRecordingSetting[];
  showStealth?: boolean;
  renderRecording: (row: RecordingPolicyRow) => ReactNode;
  renderStealth?: (row: RecordingPolicyRow) => ReactNode;
}) {
  const flat = useMemo(() => flattenRecordingPolicyRows(props.rows), [props.rows]);
  const [filters, setFilters] = useState<Filters<RecordingColumn>>({ values: new Map() });
  const counts = useMemo(() => facetCounts(flat, filters, recordingFacet, RECORDING_COLUMNS), [flat, filters]);
  const shown = useMemo(() => filterRows(flat, filters, recordingFacet, RECORDING_COLUMNS), [flat, filters]);
  const chips: FilterChip[] = [];
  for (const column of RECORDING_COLUMNS) {
    for (const value of filters.values.get(column) ?? []) {
      chips.push({ key: `${column}:${value}`, column, label: value, onRemove: () => setFilters(toggle(filters, column, value)) });
    }
  }
  const columns: DataColumn<RecordingPolicyRow>[] = [
    {
      key: "name",
      label: "Name",
      defaultWidth: 140,
      minWidth: 96,
      filter: <ColumnFilter label="Name" choices={() => choices("name", counts, filters, (value) => setFilters(toggle(filters, "name", value)))} selected={filters.values.get("name")?.size ?? 0} />,
      cell: (row) => row.name,
      rowHeader: true,
    },
    {
      key: "kind",
      label: POLICY_SCOPE_COLUMN_LABEL,
      defaultWidth: 120,
      minWidth: 96,
      filter: (
        <ColumnFilter
          label={POLICY_SCOPE_COLUMN_LABEL}
          choices={() => choices("kind", counts, filters, (value) => setFilters(toggle(filters, "kind", value)))}
          selected={filters.values.get("kind")?.size ?? 0}
        />
      ),
      cell: (row) => row.kind,
    },
    {
      key: "recording",
      label: "Recording",
      defaultWidth: 140,
      minWidth: 96,
      filter: <ColumnFilter label="Recording" choices={() => choices("recording", counts, filters, (value) => setFilters(toggle(filters, "recording", value)))} selected={filters.values.get("recording")?.size ?? 0} />,
      cell: (row) => props.renderRecording(row),
    },
    ...(props.showStealth
      ? [
          {
            key: "stealth",
            label: "Stealth Command",
            defaultWidth: 120,
            minWidth: 96,
            filter: (
              <ColumnFilter
                label="Stealth Command"
                choices={() => choices("stealth", counts, filters, (value) => setFilters(toggle(filters, "stealth", value)))}
                selected={filters.values.get("stealth")?.size ?? 0}
              />
            ),
            cell: (row: RecordingPolicyRow) => props.renderStealth?.(row) ?? null,
          } satisfies DataColumn<RecordingPolicyRow>,
        ]
      : []),
    {
      key: "effective",
      label: "Effective",
      defaultWidth: 180,
      minWidth: 120,
      filter: <ColumnFilter label="Effective" choices={() => choices("effective", counts, filters, (value) => setFilters(toggle(filters, "effective", value)))} selected={filters.values.get("effective")?.size ?? 0} />,
      cell: (row) => row.effectiveLabel,
    },
  ];
  return (
    <>
      <FilterChips chips={chips} onClearAll={() => setFilters({ values: new Map() })} />
      <div className="groups-table-wrap" aria-label="Recording">
        <DataTable className="groups-table device-table" rows={shown} rowKey={(row) => row.key} columns={columns} sort={null} onSort={() => undefined} tableId={SETTINGS_POLICIES_TABLE_ID} />
      </div>
    </>
  );
}

export function ReasonPolicyTable(props: { rows: OrganisationRecordingSetting[]; renderReason: (row: ReasonPolicyRow) => ReactNode }) {
  const flat = useMemo(() => flattenReasonPolicyRows(props.rows), [props.rows]);
  const [filters, setFilters] = useState<Filters<ReasonColumn>>({ values: new Map() });
  const counts = useMemo(() => facetCounts(flat, filters, reasonFacet, REASON_COLUMNS), [flat, filters]);
  const shown = useMemo(() => filterRows(flat, filters, reasonFacet, REASON_COLUMNS), [flat, filters]);
  const chips: FilterChip[] = [];
  for (const column of REASON_COLUMNS) {
    for (const value of filters.values.get(column) ?? []) {
      chips.push({ key: `${column}:${value}`, column, label: value, onRemove: () => setFilters(toggle(filters, column, value)) });
    }
  }
  const columns: DataColumn<ReasonPolicyRow>[] = [
    {
      key: "name",
      label: "Name",
      defaultWidth: 140,
      minWidth: 96,
      filter: <ColumnFilter label="Name" choices={() => choices("name", counts, filters, (value) => setFilters(toggle(filters, "name", value)))} selected={filters.values.get("name")?.size ?? 0} />,
      cell: (row) => row.name,
      rowHeader: true,
    },
    {
      key: "kind",
      label: POLICY_SCOPE_COLUMN_LABEL,
      defaultWidth: 120,
      minWidth: 96,
      filter: (
        <ColumnFilter
          label={POLICY_SCOPE_COLUMN_LABEL}
          choices={() => choices("kind", counts, filters, (value) => setFilters(toggle(filters, "kind", value)))}
          selected={filters.values.get("kind")?.size ?? 0}
        />
      ),
      cell: (row) => row.kind,
    },
    {
      key: "reason",
      label: "Reason",
      defaultWidth: 140,
      minWidth: 96,
      filter: <ColumnFilter label="Reason" choices={() => choices("reason", counts, filters, (value) => setFilters(toggle(filters, "reason", value)))} selected={filters.values.get("reason")?.size ?? 0} />,
      cell: (row) => props.renderReason(row),
    },
    {
      key: "effective",
      label: "Effective",
      defaultWidth: 140,
      minWidth: 96,
      filter: <ColumnFilter label="Effective" choices={() => choices("effective", counts, filters, (value) => setFilters(toggle(filters, "effective", value)))} selected={filters.values.get("effective")?.size ?? 0} />,
      cell: (row) => row.effectiveLabel,
    },
  ];
  return (
    <>
      <FilterChips chips={chips} onClearAll={() => setFilters({ values: new Map() })} />
      <div className="groups-table-wrap" aria-label="Required reason">
        <DataTable className="groups-table device-table" rows={shown} rowKey={(row) => row.key} columns={columns} sort={null} onSort={() => undefined} tableId={SETTINGS_REASON_TABLE_ID} />
      </div>
    </>
  );
}
